import { z } from 'zod';
import { isoDateTimeSchema, reasonSchema, uuidSchema } from '../common/primitives.js';
export const QUORUM_RULES = [
    'NONE',
    'MIN_PARTICIPANTS',
    'FRACTION_OF_ELIGIBLE',
    'ALL_ELIGIBLE',
];
export const quorumRuleSchema = z.enum(QUORUM_RULES);
export const APPROVAL_RULES = [
    'MAJORITY_OF_CAST',
    'MAJORITY_OF_ELIGIBLE',
    'FRACTION_OF_CAST',
    'FRACTION_OF_ELIGIBLE',
    'UNANIMOUS_OF_ELIGIBLE',
    'PLURALITY',
];
export const approvalRuleSchema = z.enum(APPROVAL_RULES);
/** `NOT_COUNTED` never invents a vote; `ABSTAIN_AFTER_DEADLINE` must be chosen explicitly. */
export const NON_RESPONSE_POLICIES = ['NOT_COUNTED', 'ABSTAIN_AFTER_DEADLINE'];
export const nonResponsePolicySchema = z.enum(NON_RESPONSE_POLICIES);
export const TIE_BREAK_RULES = ['NO_DECISION_RETURN_TO_DISCUSSION', 'ESCALATE_TO_MEETING'];
export const tieBreakRuleSchema = z.enum(TIE_BREAK_RULES);
export const VOTE_CHANGE_POLICIES = [
    'NOT_ALLOWED',
    'UNTIL_DEADLINE',
    'UNTIL_FINALIZATION',
];
export const voteChangePolicySchema = z.enum(VOTE_CHANGE_POLICIES);
export const LATE_VOTE_POLICIES = ['REJECT', 'ACCEPT_UNTIL_FINALIZATION'];
export const lateVotePolicySchema = z.enum(LATE_VOTE_POLICIES);
export const VOTE_VISIBILITIES = ['VISIBLE_AFTER_SUBMISSION', 'HIDDEN_UNTIL_CLOSE'];
export const voteVisibilitySchema = z.enum(VOTE_VISIBILITIES);
export const VOTING_WEIGHT_BASES = ['ONE_PER_MEMBER', 'EQUITY_PERCENTAGE'];
export const votingWeightBasisSchema = z.enum(VOTING_WEIGHT_BASES);
export const ELIGIBILITY_RULES = [
    'ACTIVE_FOUNDERS',
    'ACTIVE_FOUNDERS_AND_GRANTED_MEMBERS',
];
export const eligibilityRuleSchema = z.enum(ELIGIBILITY_RULES);
export const POLICY_VERSION_STATUSES = ['PROPOSED', 'APPROVED', 'REJECTED', 'WITHDRAWN'];
export const policyVersionStatusSchema = z.enum(POLICY_VERSION_STATUSES);
/** Exact fraction in (0, 1] with up to six decimals, e.g. `0.666667`. Never a JS float. */
export const fractionSchema = z
    .string()
    .trim()
    .regex(/^(0(\.\d{1,6})?|1(\.0{1,6})?)$/, 'Use a decimal between 0 and 1, e.g. 0.6')
    .refine((value) => !/^0(\.0*)?$/.test(value), { message: 'Must be greater than 0' });
const FRACTION_APPROVAL_RULES = [
    'FRACTION_OF_CAST',
    'FRACTION_OF_ELIGIBLE',
];
export const policyRulesSchema = z.object({
    quorumRule: quorumRuleSchema,
    quorumMinParticipants: z.number().int().min(1).max(1000).nullable(),
    quorumFraction: fractionSchema.nullable(),
    approvalRule: approvalRuleSchema,
    approvalFraction: fractionSchema.nullable(),
    abstentionsCountTowardQuorum: z.boolean(),
    nonResponsePolicy: nonResponsePolicySchema,
    tieBreakRule: tieBreakRuleSchema,
    voteChangePolicy: voteChangePolicySchema,
    lateVotePolicy: lateVotePolicySchema,
    voteVisibility: voteVisibilitySchema,
    votingWeightBasis: votingWeightBasisSchema,
    eligibilityRule: eligibilityRuleSchema,
    autoFinalize: z.boolean(),
    reopenRequiresReason: z.boolean(),
});
/** Mirrors the database CHECK constraints so errors surface on the right field. */
export const validPolicyRulesSchema = policyRulesSchema
    .refine((rules) => (rules.quorumRule === 'MIN_PARTICIPANTS') === (rules.quorumMinParticipants !== null), {
    message: 'Minimum participants is required for (and only for) the "minimum participants" quorum',
    path: ['quorumMinParticipants'],
})
    .refine((rules) => (rules.quorumRule === 'FRACTION_OF_ELIGIBLE') === (rules.quorumFraction !== null), {
    message: 'A quorum fraction is required for (and only for) the "fraction of eligible" quorum',
    path: ['quorumFraction'],
})
    .refine((rules) => FRACTION_APPROVAL_RULES.includes(rules.approvalRule) === (rules.approvalFraction !== null), {
    message: 'An approval fraction is required for (and only for) fraction-based approval rules',
    path: ['approvalFraction'],
});
export const policyVersionSchema = policyRulesSchema.extend({
    id: uuidSchema,
    policyId: uuidSchema,
    versionNumber: z.number().int(),
    status: policyVersionStatusSchema,
    effectiveFrom: isoDateTimeSchema.nullable(),
    notes: z.string().nullable(),
    proposedBy: z.string().nullable(),
    approvalDecisionId: uuidSchema.nullable(),
    approvalReference: z.string().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    rejectedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const governancePolicySchema = z.object({
    id: uuidSchema,
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    /** Latest APPROVED version already in effect, or `null` (rounds cannot open under this policy). */
    currentVersion: policyVersionSchema.nullable(),
    versions: z.array(policyVersionSchema),
    /** Categories whose topics must use this policy. */
    categories: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export const governancePolicyListSchema = z.object({ items: z.array(governancePolicySchema) });
export const proposePolicyVersionRequestSchema = validPolicyRulesSchema.and(z.object({ notes: z.string().trim().max(5000).nullable().optional() }));
/** Governance configuration needs evidence: a finalized decision or an external approval record. */
export const approvalEvidenceSchema = z
    .object({
    decisionId: uuidSchema.nullable().optional(),
    approvalReference: z.string().trim().min(3).max(500).nullable().optional(),
})
    .refine((value) => (value.decisionId ?? null) !== null || (value.approvalReference ?? null) !== null, {
    message: 'Link the approving decision or record the approval reference',
    path: ['approvalReference'],
});
export const approvePolicyVersionRequestSchema = approvalEvidenceSchema.and(z.object({
    /** Defaults to now; must not be in the past. */
    effectiveFrom: isoDateTimeSchema.optional(),
}));
export const closePolicyVersionRequestSchema = z.object({ reason: reasonSchema });
export const setCategoryPolicyRequestSchema = approvalEvidenceSchema.and(z.object({
    categoryId: uuidSchema,
    /** `null` removes the mapping (topics fall back to the `standard` policy). */
    policyId: uuidSchema.nullable(),
    reason: reasonSchema,
}));
//# sourceMappingURL=policies.js.map